/**
 * Notification System Types
 *
 * Two-level classification:
 * - `kind`: UI display styling (color, icon)
 * - `type`: Business event (what happened)
 *
 * @see NOTIFICATION_SYSTEM.md for full documentation
 */

// =============================================================================
// NotificationKind - UI Display Styling
// =============================================================================

export type NotificationKind =
  | 'info'      // Blue - general information
  | 'success'   // Green - positive outcomes
  | 'warning'   // Orange - attention needed
  | 'error'     // Red - failures
  | 'message';  // Blue with chat icon - chat messages

export const NOTIFICATION_KIND_CONFIG: Record<NotificationKind, {
  icon: string;
  iconColor: string;
  toastType: 'info' | 'success' | 'warning' | 'error';
}> = {
  info: {
    icon: 'info',
    iconColor: 'tw-text-blue-500',
    toastType: 'info',
  },
  success: {
    icon: 'check_circle',
    iconColor: 'tw-text-green-500',
    toastType: 'success',
  },
  warning: {
    icon: 'warning',
    iconColor: 'tw-text-orange-500',
    toastType: 'warning',
  },
  error: {
    icon: 'error',
    iconColor: 'tw-text-red-500',
    toastType: 'error',
  },
  message: {
    icon: 'chat',
    iconColor: 'tw-text-blue-500',
    toastType: 'info',
  },
};

// =============================================================================
// NotificationType - Business Events
// =============================================================================

export type NotificationType =
  // Chat & Collaboration
  | 'NEW_MESSAGE'
  | 'MESSAGE_FROM_TEAMMATE'
  | 'COLLABORATION_QUEUED'
  | 'COLLABORATION_ASSIGNED'
  // Orders
  | 'ORDER_NEW'
  | 'ORDER_PAID'
  | 'ORDER_SHIPPED'
  | 'ORDER_COMPLETED'
  | 'ORDER_CANCELLED'
  // Sync & System
  | 'SYNC_COMPLETED'
  | 'SYNC_FAILED'
  | 'TOKEN_EXPIRED'
  | 'SYSTEM_ALERT';

// Default kind for each notification type
export const DEFAULT_KIND_BY_TYPE: Record<NotificationType, NotificationKind> = {
  // Chat & Collaboration
  NEW_MESSAGE: 'message',
  MESSAGE_FROM_TEAMMATE: 'info',
  COLLABORATION_QUEUED: 'info',
  COLLABORATION_ASSIGNED: 'success',
  // Orders
  ORDER_NEW: 'success',
  ORDER_PAID: 'success',
  ORDER_SHIPPED: 'info',
  ORDER_COMPLETED: 'success',
  ORDER_CANCELLED: 'warning',
  // Sync & System
  SYNC_COMPLETED: 'success',
  SYNC_FAILED: 'error',
  TOKEN_EXPIRED: 'warning',
  SYSTEM_ALERT: 'info',
};

// Default action URL for each notification type
export const DEFAULT_ACTION_URL_BY_TYPE: Partial<Record<NotificationType, string>> = {
  NEW_MESSAGE: '/chat',
  MESSAGE_FROM_TEAMMATE: '/chat',
  COLLABORATION_QUEUED: '/ciq',
  COLLABORATION_ASSIGNED: '/chat',
  ORDER_NEW: '/orders',
  ORDER_PAID: '/orders',
  ORDER_SHIPPED: '/orders',
  ORDER_COMPLETED: '/orders',
  ORDER_CANCELLED: '/orders',
  SYNC_COMPLETED: '/products',
  SYNC_FAILED: '/settings/accounts',
  TOKEN_EXPIRED: '/settings/accounts',
};

// =============================================================================
// Legacy Type Mapping (for backward compatibility)
// =============================================================================

export type LegacyNotificationType =
  | 'collaborations'
  | 'messages'
  | 'syncOrder'
  | 'ORDER_UPDATE'
  | 'TOKEN_EXPIRED'
  | 'GENERAL'
  | 'SYSTEM';

export type LegacyStatus =
  | 'queue'
  | 'handled'
  | 'inbound'
  | 'outbound';

/**
 * Maps legacy Firestore type+status to new NotificationType
 */
export function mapLegacyToNotificationType(
  legacyType: string,
  status?: string
): NotificationType {
  switch (legacyType) {
    case 'collaborations':
      return status === 'handled' ? 'COLLABORATION_ASSIGNED' : 'COLLABORATION_QUEUED';
    case 'messages':
      return status === 'outbound' ? 'MESSAGE_FROM_TEAMMATE' : 'NEW_MESSAGE';
    case 'syncOrder':
      return 'SYNC_COMPLETED';
    case 'ORDER_UPDATE':
      return 'ORDER_NEW'; // Can be refined based on order status
    case 'TOKEN_EXPIRED':
      return 'TOKEN_EXPIRED';
    case 'GENERAL':
    case 'SYSTEM':
    default:
      return 'SYSTEM_ALERT';
  }
}

// =============================================================================
// Notification Interface
// =============================================================================

export interface Notification {
  id: string;
  type: NotificationType;
  kind: NotificationKind;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;

  // UI customization (optional, defaults from kind)
  icon?: string;
  iconColor?: string;

  // Action
  actionUrl?: string;
  actionLabel?: string;

  // Context references
  channel?: string;
  collaborationId?: string;
  roomId?: string;
  orderId?: string;
  customerId?: string;
  companyId?: string;
  companyName?: string;

  // Sender info (for messages)
  senderName?: string;
  senderAvatar?: string;
  phoneNumber?: string;
}

// Backward compatibility alias
export type ModernNotification = Notification;

// =============================================================================
// State & Actions
// =============================================================================

export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error?: string;
}

export interface NotificationActions {
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  deleteAllNotifications: () => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
}

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create a notification with sensible defaults
 */
export function createNotification(
  type: NotificationType,
  title: string,
  message: string,
  overrides?: Partial<Notification>
): Omit<Notification, 'id'> {
  const kind = overrides?.kind ?? DEFAULT_KIND_BY_TYPE[type];
  const kindConfig = NOTIFICATION_KIND_CONFIG[kind];

  return {
    type,
    kind,
    title,
    message,
    timestamp: new Date(),
    read: false,
    icon: kindConfig.icon,
    iconColor: kindConfig.iconColor,
    actionUrl: DEFAULT_ACTION_URL_BY_TYPE[type],
    ...overrides,
  };
}

// Convenience factories
export const createMessageNotification = (
  senderName: string,
  messagePreview: string,
  options?: { channel?: string; collaborationId?: string; roomId?: string }
): Omit<Notification, 'id'> =>
  createNotification('NEW_MESSAGE', senderName, messagePreview, {
    senderName,
    ...options,
  });

export const createOrderNotification = (
  type: 'ORDER_NEW' | 'ORDER_PAID' | 'ORDER_SHIPPED' | 'ORDER_COMPLETED' | 'ORDER_CANCELLED',
  orderNumber: string,
  message: string,
  orderId?: string
): Omit<Notification, 'id'> =>
  createNotification(type, `Order #${orderNumber}`, message, { orderId });

export const createSyncNotification = (
  storeName: string,
  success: boolean = true
): Omit<Notification, 'id'> =>
  createNotification(
    success ? 'SYNC_COMPLETED' : 'SYNC_FAILED',
    success ? 'Sync Completed' : 'Sync Failed',
    success
      ? `Products from '${storeName}' have been synced.`
      : `Failed to sync products from '${storeName}'.`
  );

export const createTokenExpiredNotification = (
  accountName: string
): Omit<Notification, 'id'> =>
  createNotification(
    'TOKEN_EXPIRED',
    'Token Expired',
    `Please reconnect your ${accountName} account.`
  );
